import { createSlice } from '@reduxjs/toolkit';
import _uniq from 'lodash/uniq';
import moment from 'moment';
import i18n from '../app/i18n';
import { SnackBarType } from '../app/types';
import { asyncForEach, validateMax, validateText } from '../app/utils';
import AiBotService from '../services/AiBotService';
import errorHandle from './errorHandle';
import { enqueueSnackbar } from './notifierSlice';

export const aiBotSlice = createSlice({
  name: 'aiBot',
  initialState: {
    pathKey: 'aiBot',
    data: {
      name: `Super Agent ${moment().format('YYYY-MM-DD HH:mm')}`,
      model: 'gpt-3.5-turbo',
      provider: 'azure',
      platforms: [],
      keyword: '',
      hitTags: [],
      welcomeMessage: `我會即時撈取資料庫生成內容、會受到 ChatGPT 全球使用量的影響速度，可能需要稍微等候一下。

關於「推薦類型」提問是目前我最不擅長的題型，建議先跳過，也能節省你寶貴的時間 ！
      
👉 最後，想結束 AI 對話時，可針對「已解決，結束 AI 對話」輸入關鍵字或按下按鈕。如果您覺得我回答的不夠精準，可針對「尚未解決，洽詢專員」關鍵字或按下按鈕，並由專人來為您服務。`,
      rejectReply: {
        enabled: false,
        message: '已轉介專員，請稍候！',
      },
      continueMessage: {
        imageType: 'upload',
        imageUrl: '',
        title: '',
        subtitle: '',
        buttons: [
          {
            type: 'message',
            title: '已解決，結束 AI 對話',
            text: '已解決，結束 AI 對話',
            tags: [],
          },
          {
            type: 'message',
            title: '尚未解決，洽詢專員',
            text: '尚未解決，洽詢專員',
            inbox: 'unassigned',
            displayName: '',
            userId: '',
            tags: [],
            botId: '',
          },
        ],
      },
      oosMessage: {
        enabled: false,
        value: `親愛的用戶，感謝您使用聊天 Agent 服務。目前由於內部因素，我們暫停了此 Super Agent 的功能，同時也正積極解決這個問題。

如果您有任何疑問或需要幫助，請隨時聯絡我們的客服團隊，我們將竭誠為您服務。`,
      },
      brandName: '',
      roleDescription: {
        enabled: false,
        value: ``,
      },
      noAnswerMessage:
        '非常抱歉，您的問題在資料庫內找尋不到相應答案，請您換個問題吧～',
      noFileMessage:
        '非常抱歉，我目前無法判讀圖片、影片、檔案等，請您用文字與我溝通。',
      replyTemplate: {
        enabled: false,
        value: `您好，謝謝您的提問！關於覆述客戶問題
目前就我的理解：
1. 答案1
2. 答案2`,
      },
      replyRule: {
        enabled: false,
        value: ``,
      },
      replySourceUrl: {
        enabled: true,
        wording: '參考網址：',
      },
      competitors: {
        enabled: false,
        values: [],
        replyMessage: `不好意思！我是「客戶品牌名稱」的智能 AI，僅能為您提供本品牌相關服務，無法為您提供其他品牌的服務！`,
      },
      autoExit: {
        enabled: true,
        interval: 600,
        message: `由於您久未與 Agent 持續進行互動，系統已自動退出對話。如果您想重新啟動 Agent，請重新觸發關鍵字～`,
        inbox: 'unassigned',
      },
      endDetection: {
        enabled: false,
        message: '感謝您的支持，祝您今天愉快，期待下次為您服務！',
      },
      rateLimit: {
        enabled: false,
        limit: 100,
        message:
          '由於您的操作頻率過於頻繁，現已強制退出 Agent 流程，請稍後再試。',
      },
      reflectMode: {
        enabled: true,
      },
      dataset: [],
      status: 'on',
    },
    isLoading: false,
    isSaved: false,
    isActive: true,
    progress: {
      done: true,
      complete: 0,
      total: 0,
    },
    correctionList: [],
    totalCorrectionWord: {
      total: 0,
      tuning: 0,
    },
    isSaveDraft: false,
    datasets: [],
    // 尚未導入的資料集
    draftDatasets: [],
    // 新增網址、文件時資料解析的進度狀態
    datasetInitStatus: {
      total: 0,
      complete: 0,
      done: true,
    },
    client: null,
    clientReady: false,
    datasetUsed: 0,
    tuningUsed: 0,
  },
  reducers: {
    setPathKey: (state, action) => ({
      ...state,
      pathKey: action.payload,
    }),
    updateDatasset(state, action) {
      action.payload.forEach(newItem => {
        const index = state.datasets.findIndex(item => item.id === newItem.id);
        if (index !== -1) {
          state.datasets[index] = newItem;
        }
      });
    },
    setDatasetInitStatus: (state, action) => ({
      ...state,
      datasetInitStatus: action.payload,
    }),
    setClient: (state, action) => ({
      ...state,
      client: action.payload,
    }),
    setClientReady: (state, action) => ({
      ...state,
      clientReady: action.payload,
    }),
    setDraftDatasets: (state, action) => ({
      ...state,
      draftDatasets: action.payload,
    }),
    addDraftDatasets: (state, action) => ({
      ...state,
      draftDatasets: [action.payload, ...state.draftDatasets],
    }),
    setDatasets: (state, action) => ({
      ...state,
      datasets: action.payload,
    }),
    setIsSaveDraft: (state, action) => ({
      ...state,
      isSaveDraft: action.payload,
    }),
    setIsActive: (state, action) => ({
      ...state,
      isActive: action.payload,
    }),
    setIsSaved: (state, action) => ({
      ...state,
      isSaved: action.payload,
    }),
    setIsLoading: (state, action) => ({
      ...state,
      isLoading: action.payload,
    }),
    setProgress: (state, action) => ({
      ...state,
      progress: action.payload,
    }),
    setData: (state, action) => ({
      ...state,
      data: {
        ...state.data,
        ...action.payload,
      },
    }),
    setCorrectionList: (state, action) => ({
      ...state,
      correctionList: action.payload,
    }),
    setState: (state, action) => ({
      ...state,
      ...action.payload,
    }),
  },
});

export const {
  setPathKey,
  addDraftDatasets,
  setDraftDatasets,
  setDatasetInitStatus,
  updateDatasset,
  setClient,
  setClientReady,
  setDatasets,
  setIsSaveDraft,
  setCorrectionList,
  setProgress,
  setIsSaved,
  setIsLoading,
  setData,
  setState,
  setIsActive,
} = aiBotSlice.actions;

export const checkContinueMessage = continueMessage => {
  return continueMessage?.buttons.every(
    b => (b?.inbox === 'private' ? b?.userId : true) && !!b?.title,
  );
};

export const checkBasicSetting = data => {
  const { name, platforms } = data || {};

  return platforms.length > 0 && validateText(name);
};

export const checkTriggerSetting = data => {
  const {
    keyword,
    welcomeMessage,
    continueMessage,
    oosMessage,
    autoExit,
    rateLimit,
    rejectReply,
  } = data || {};

  return (
    validateText(keyword) &&
    validateText(welcomeMessage) &&
    validateMax(welcomeMessage, 600) &&
    checkContinueMessage(continueMessage) &&
    (autoExit?.enabled
      ? validateText(autoExit?.message) &&
        autoExit?.interval &&
        (autoExit?.inbox === 'private' ? autoExit?.userId : true)
      : true) &&
    (rateLimit?.enabled
      ? validateText(rateLimit?.message) && rateLimit?.limit > 0
      : true) &&
    (oosMessage?.enabled ? validateText(oosMessage?.value) : true) &&
    (rejectReply?.enabled ? validateText(rejectReply?.message) : true)
  );
};

export const checkAdvanceSetting = data => {
  const {
    brandName,
    roleDescription,
    noAnswerMessage,
    noFileMessage,
    replyTemplate,
    replySourceUrl,
    replyRule,
  } = data || {};

  return (
    validateText(brandName) &&
    validateMax(brandName, 20) &&
    validateText(noAnswerMessage) &&
    validateMax(noAnswerMessage, 80) &&
    validateText(noFileMessage) &&
    validateMax(noFileMessage, 80) &&
    (roleDescription?.enabled
      ? validateText(roleDescription?.value) &&
        validateMax(roleDescription?.value, 120)
      : true) &&
    (replyTemplate?.enabled
      ? validateText(replyTemplate?.value) &&
        validateMax(replyTemplate?.value, 160)
      : true) &&
    (replySourceUrl?.enabled ? validateText(replySourceUrl?.wording) : true) &&
    (replyRule?.enabled
      ? validateText(replyRule?.value) && validateMax(replyRule?.value, 500)
      : true)
  );
};

export const checkData = (data, progress) => {
  const isBasicSettingVerify = checkBasicSetting(data);
  const isTriggerSettingVerify = checkTriggerSetting(data);
  const isAdvanceSettingVerify = checkAdvanceSetting(data);

  return (
    isBasicSettingVerify &&
    isTriggerSettingVerify &&
    isAdvanceSettingVerify &&
    progress?.done
  );
};

export const getData = (orgId, id) => async (dispatch, getState) => {
  try {
    dispatch(setIsLoading(true));
    const { integration = [] } = getState().organization;
    const { pathKey } = getState().aiBot;
    const response = await AiBotService.getData(orgId, id);

    dispatch(
      setData({
        ...response,
        ...(response?.roleDescription
          ? {
              roleDescription: {
                enabled:
                  typeof response?.roleDescription === 'string'
                    ? true
                    : response?.roleDescription?.enabled,
                value:
                  typeof response?.roleDescription === 'string'
                    ? response?.roleDescription
                    : response?.roleDescription?.value,
              },
            }
          : {}),
        rejectReply: {
          enabled: false,
          message: i18n.t(`${pathKey}:defaultRejectReplyMessage`),
          ...response?.rejectReply,
        },
        replyRule: {
          enabled: false,
          value: '',
          ...response?.replyRule,
        },
        autoExit: {
          enabled: true,
          interval: 600,
          message: i18n.t(`${pathKey}:defalutAutoExitMessage`),
          inbox: 'unassigned',
          ...response?.autoExit,
        },
        competitors: {
          enabled: false,
          values: [],
          replyMessage: i18n.t(`${pathKey}:defalutCompetitorMessage`),
          ...response?.competitors,
        },
        continueMessage: {
          imageType: 'upload',
          imageUrl: '',
          title: '',
          subtitle: '',
          buttons: [
            {
              type: 'message',
              title: i18n.t(`${pathKey}:defalutContinueMessage1`),
              text: i18n.t(`${pathKey}:defalutContinueMessage1`),
              tags: [],
            },
            {
              type: 'message',
              title: i18n.t(`${pathKey}:defalutContinueMessage2`),
              text: i18n.t(`${pathKey}:defalutContinueMessage2`),
              inbox: 'unassigned',
              userId: '',
              tags: [],
            },
          ],
          ...response?.continueMessage,
        },
        noAnswerMessage: response?.noAnswerMessage
          ? response?.noAnswerMessage
          : i18n.t(`${pathKey}:defaultNoAnswerMessage`),
        noFileMessage: response?.noFileMessage
          ? response?.noFileMessage
          : i18n.t(`${pathKey}:defaultNoFileMessage`),
        oosMessage: {
          enabled: false,
          value: i18n.t(`${pathKey}:defaultOosMessageMessage`),
          ...response?.oosMessage,
        },
        replySourceUrl: {
          enabled: true,
          wording: i18n.t(`${pathKey}:defaultReplySourceUrlMessage`),
          ...response?.replySourceUrl,
        },
        replyTemplate: {
          enabled: false,
          value: i18n.t(`${pathKey}:defaultReplyTemplateMessage`),
          ...response?.replyTemplate,
        },
        welcomeMessage: response?.welcomeMessage
          ? response?.welcomeMessage
          : i18n.t(`${pathKey}:defaultWelcomeMessage`),
        rateLimit: {
          enabled: false,
          limit: 100,
          message: i18n.t(
            `${pathKey}:triggerSettingContainer.defaultRateLimitMesssage`,
          ),
          ...response?.rateLimit,
        },
        endDetection: {
          enabled: false,
          message: i18n.t(`${pathKey}:endDetectionMessage`),
          ...response?.endDetection,
        },
        platforms: response?.platforms
          ? response?.platforms?.filter(p =>
              integration
                ? !!integration.find(i => i?.platform?.objectId === p)
                : false,
            )
          : [],
      }),
    );
    dispatch(setIsActive(response?.status === 'on'));

    dispatch(setIsLoading(false));
  } catch (error) {
    dispatch(setData({}));
    dispatch(setIsLoading(false));
    dispatch(errorHandle(error));
  }
};

export const saveData = (orgId, id) => async (dispatch, getStore) => {
  try {
    dispatch(setIsLoading(true));
    dispatch(setIsSaved(true));

    const { data } = getStore().aiBot;

    await AiBotService.updateData(orgId, id, data);

    dispatch(
      enqueueSnackbar({
        variant: SnackBarType.SUCCESS,
        message: i18n.t(`success-save`),
      }),
    );

    dispatch(setData({ isDraft: true }));
    dispatch(setIsSaveDraft(false));
    dispatch(setIsLoading(false));
  } catch (error) {
    dispatch(setIsLoading(false));
    dispatch(errorHandle(error));
  }
};

export const publishData = (orgId, id, force) => async (dispatch, getStore) => {
  try {
    dispatch(setIsLoading(true));
    dispatch(setIsSaved(true));

    const { data, progress } = getStore().aiBot;

    const isDataVerify = checkData(data, progress);

    if (isDataVerify) {
      await AiBotService.updateData(orgId, id, data);
      await AiBotService.publishData(orgId, id, force);

      dispatch(setData({ isDraft: false }));
      dispatch(setIsSaveDraft(false));
      dispatch(setIsActive(true));
      dispatch(
        enqueueSnackbar({
          variant: SnackBarType.SUCCESS,
          message: i18n.t(`success-publish`),
        }),
      );
    } else {
      dispatch(errorHandle('data-error'));
    }

    dispatch(setIsLoading(false));
  } catch (error) {
    dispatch(setIsLoading(false));
    dispatch(errorHandle(error, false));
  }
};

export const getDatasetUsed = (orgId, id) => async dispatch => {
  try {
    const used = await AiBotService.getUsed(orgId, id);

    dispatch(setState({ datasetUsed: used }));

    return used;
  } catch (error) {
    dispatch(errorHandle(error));
    return 0;
  }
};

export const getTuningUsedUsed = (orgId, id) => async dispatch => {
  try {
    const used = await AiBotService.getUsed(orgId, id, 'tuning');

    dispatch(setState({ tuningUsed: used }));

    return used;
  } catch (error) {
    dispatch(errorHandle(error));
    return 0;
  }
};

export const deleteDatasets =
  (orgId, id, datasetIds, dataType, isSelectAll) =>
  async (dispatch, getStore) => {
    try {
      const { datasets, pathKey } = getStore().aiBot;

      await AiBotService.deleteDataset(
        orgId,
        id,
        datasetIds,
        dataType,
        isSelectAll,
      );

      dispatch(
        setData({
          isDraft: true,
        }),
      );

      dispatch(
        setState({
          datasets: datasets.filter(
            d => !datasetIds.find(datasetId => datasetId === d?.id),
          ),
        }),
      );

      await dispatch(getDatasetUsed(orgId, id));
      dispatch(
        enqueueSnackbar({
          variant: SnackBarType.SUCCESS,
          message: i18n.t(`${pathKey}:deleteDataset-modal-success`),
        }),
      );
    } catch (error) {
      dispatch(errorHandle(error));
    }
  };

// 新增單一網址
const addWebToDataset = (orgId, id, url) => async dispatch => {
  const response = await AiBotService.addUrl(orgId, id, url);
  if (window.location.href.includes('/knowledge/addWeb')) {
    dispatch(addDraftDatasets(response));
  }

  return response;
};

// 新增多個網址
const addBatchUrls = (orgId, id, list) => async (dispatch, getStore) => {
  const { draftDatasets } = getStore().aiBot;

  const urls = _uniq(
    list.filter(url => !draftDatasets.some(d => d.url === url)),
  );
  const total = urls.length;
  dispatch(
    setDatasetInitStatus({
      done: false,
      total,
      complete: 0,
    }),
  );
  if (urls && urls.length) {
    let complete = 0;
    await asyncForEach(urls, async v => {
      await dispatch(addWebToDataset(orgId, id, v));
      complete += 1;
      dispatch(
        setDatasetInitStatus({
          total,
          done: complete === total,
          complete,
        }),
      );
    });
  } else {
    dispatch(
      setDatasetInitStatus({
        done: true,
      }),
    );
  }
};

export const addDataset =
  (orgId, id, value, type = 'file') =>
  async (dispatch, getStore) => {
    try {
      const { draftDatasets } = getStore().aiBot;

      let response = null;
      switch (type) {
        case 'file':
          dispatch(setDatasetInitStatus({ done: false }));

          const formData = new FormData();

          formData.append('file', value);
          formData.append('orgId', orgId);

          response = await AiBotService.addFile(id, formData);

          dispatch(
            setState({
              draftDatasets: [
                {
                  ...response,
                  name: value?.name || value,
                },
                ...draftDatasets,
              ],
            }),
          );
          dispatch(setDatasetInitStatus({ done: true }));
          break;
        case 'batchUrls':
          await dispatch(addBatchUrls(orgId, id, value));
          break;
        case 'sitemap':
          const list = await AiBotService.getSiteList(orgId, id, value);
          await dispatch(addBatchUrls(orgId, id, list));
          break;
        case 'web':
          dispatch(
            setDatasetInitStatus({ done: false, total: 1, complete: 0 }),
          );
          await dispatch(addWebToDataset(orgId, id, value));
          dispatch(setDatasetInitStatus({ done: true }));
          break;
        default:
          break;
      }
    } catch (error) {
      dispatch(errorHandle(error));
      dispatch(setDatasetInitStatus({ done: true }));
    }
  };

export const simulate = (orgId, id, question) => async dispatch => {
  try {
    const response = await AiBotService.simulate(orgId, id, question);

    return response;
  } catch (error) {
    dispatch(errorHandle(error));
    return null;
  }
};

export const getDatasetProgress = (orgId, id) => async dispatch => {
  try {
    const response = await AiBotService.getDatasetProgress(orgId, id);

    dispatch(setProgress(response));

    return response;
  } catch (error) {
    dispatch(errorHandle(error));
    return null;
  }
};

export const embeddings =
  (orgId, id, dataType) => async (dispatch, getStore) => {
    try {
      const { pathKey } = getStore().aiBot;
      const response = await AiBotService.embeddings(orgId, id, dataType);

      await dispatch(getDatasetProgress(orgId, id));
      await dispatch(getDatasetUsed(orgId, id));

      dispatch(setData({ isDraft: true }));
      dispatch(setState({ draftDatasets: [] }));
      dispatch(
        enqueueSnackbar({
          variant: SnackBarType.SUCCESS,
          message: i18n.t(`${pathKey}:embeddings-modal-process`),
        }),
      );

      return response;
    } catch (error) {
      dispatch(errorHandle(error));
      return null;
    }
  };

export const getPoints = orgId => async dispatch => {
  try {
    const response = await AiBotService.getPoints(orgId);

    return response?.point || 0;
  } catch (error) {
    dispatch(errorHandle(error));
    return 0;
  }
};

export const checkPoints = orgId => async dispatch => {
  try {
    const response = await AiBotService.getPoints(orgId);
    const point = response?.point || 0;

    if (point <= 20 && point > 0) {
      dispatch(errorHandle('error/usage_limit'));
    } else if (point <= 0) {
      dispatch(errorHandle('error/usage_limit_exceeded'));
    }
  } catch (error) {
    dispatch(errorHandle(error));
  }
};

export const getCorrectionListInit = (orgId, id) => async dispatch => {
  try {
    dispatch(setIsLoading(true));

    const response = await AiBotService.getTuning(orgId, id);
    dispatch(setCorrectionList(response));

    dispatch(setIsLoading(false));
  } catch (error) {
    dispatch(errorHandle(error));
    dispatch(setIsLoading(false));
  }
};

export const getCorrectionLisContinue =
  (orgId, id) => async (dispatch, getStore) => {
    try {
      const { correctionList } = getStore().aiBot;

      dispatch(setIsLoading(true));

      const response = await AiBotService.getTuning(
        orgId,
        id,
        correctionList.length,
        20,
      );
      dispatch(setCorrectionList([...correctionList, ...response]));

      dispatch(setIsLoading(false));

      return response.length >= 20;
    } catch (error) {
      dispatch(errorHandle(error));
      dispatch(setIsLoading(false));
      return false;
    }
  };

export const addOrUpdateCorrection =
  (orgId, id, data) => async (dispatch, getStore) => {
    try {
      const { correctionList, pathKey } = getStore().aiBot;

      dispatch(setIsLoading(true));

      if (data?.id) {
        const response = await AiBotService.updateTuning(orgId, id, data?.id, {
          question: data?.question,
          newAnswer: data?.newAnswer,
          newSourceUrl: data?.newSourceUrl,
        });

        dispatch(
          setCorrectionList(
            correctionList.map(c => {
              if (c?.id === data?.id) {
                return {
                  ...c,
                  ...response,
                };
              }
              return c;
            }),
          ),
        );

        dispatch(
          enqueueSnackbar({
            variant: SnackBarType.SUCCESS,
            message: i18n.t(`${pathKey}:updateCorrectionSuccess`),
          }),
        );
      } else {
        const response = await AiBotService.addTuning(orgId, id, {
          question: data?.question,
          originalAnswer: data?.originalAnswer,
          newAnswer: data?.newAnswer,
          newSourceUrl: data?.newSourceUrl,
        });

        dispatch(setCorrectionList([response, ...correctionList]));

        dispatch(
          enqueueSnackbar({
            variant: SnackBarType.SUCCESS,
            message: i18n.t(`${pathKey}:addCorrectionSuccess`),
          }),
        );
      }

      await dispatch(getTuningUsedUsed(orgId, id));
      dispatch(setData({ isDraft: true }));
      dispatch(setIsLoading(false));
    } catch (error) {
      dispatch(errorHandle(error));
      dispatch(setIsLoading(false));
    }
  };

export const deleteCorrection =
  (orgId, id, datasetId) => async (dispatch, getStore) => {
    try {
      const { correctionList, pathKey } = getStore().aiBot;

      dispatch(setIsLoading(true));

      await AiBotService.deleteTuning(orgId, id, [datasetId]);

      dispatch(
        setCorrectionList(correctionList.filter(c => c?.id !== datasetId)),
      );

      await dispatch(getTuningUsedUsed(orgId, id));

      dispatch(
        enqueueSnackbar({
          variant: SnackBarType.SUCCESS,
          message: i18n.t(`${pathKey}:deleteCorrectionSuccess`),
        }),
      );

      dispatch(setData({ isDraft: true }));
      dispatch(setIsLoading(false));
    } catch (error) {
      dispatch(errorHandle(error));
      dispatch(setIsLoading(false));
    }
  };

export const refreshDataset =
  (orgId, id, datasetIds, dataType, isSelectAll) =>
  async (dispatch, getStore) => {
    try {
      const { pathKey } = getStore().aiBot;

      await AiBotService.refreshDataset(
        orgId,
        id,
        datasetIds,
        dataType,
        isSelectAll,
      );

      await dispatch(getDatasetProgress(orgId, id));

      dispatch(setData({ isDraft: true }));
      dispatch(
        enqueueSnackbar({
          variant: SnackBarType.SUCCESS,
          message: i18n.t(`${pathKey}:embeddings-modal-process`),
        }),
      );
    } catch (error) {
      dispatch(errorHandle(error));
    }
  };

// isContinue時，若get list result length = 0，則lazy load不在執行
let isNoOther = false;

export const getDatasetListContinue =
  (orgId, id, query) => async (dispatch, getStore) => {
    try {
      if (!isNoOther) {
        const { datasets } = getStore().aiBot;
        const dataLength = datasets?.length || 0;

        const results = await AiBotService.getDatasetList(
          orgId,
          id,
          query,
          dataLength,
          20,
        );

        isNoOther = !results.length;

        dispatch(
          setState({
            datasets: [...datasets, ...results],
          }),
        );
        return [...datasets, ...results];
      }
      return [];
    } catch (error) {
      isNoOther = true;
      dispatch(errorHandle(error));
      return [];
    }
  };

export const getDatasetListInit = (orgId, id, query) => async dispatch => {
  try {
    dispatch(
      setState({
        datasets: [],
        isLoading: true,
      }),
    );

    const datasets = await AiBotService.getDatasetList(orgId, id, query, 0, 20);

    dispatch(
      setState({
        datasets,
        isLoading: false,
      }),
    );

    isNoOther = false;
  } catch (error) {
    dispatch(
      setState({
        isLoading: false,
      }),
    );

    dispatch(errorHandle(error));
  }
};

export const saveScheduledUpdatesSetting =
  (orgId, id, data) => async dispatch => {
    try {
      await AiBotService.saveScheduledUpdatesSetting(orgId, id, data);

      await dispatch(getData(orgId, id));

      dispatch(
        enqueueSnackbar({
          variant: SnackBarType.SUCCESS,
          message: i18n.t(`success-save`),
        }),
      );
    } catch (error) {
      dispatch(errorHandle(error));
    }
  };

export const getDraftDatase = (orgId, id, dataType) => async dispatch => {
  try {
    const draftDatasets = await AiBotService.getDraftDatase(
      orgId,
      id,
      dataType,
    );

    dispatch(
      setState({
        draftDatasets,
      }),
    );
  } catch (error) {
    dispatch(errorHandle(error));
  }
};

export const getDatasetListTotal = (orgId, id, query) => async dispatch => {
  try {
    const total = await AiBotService.getDatasetListTotal(orgId, id, query);
    return total || 0;
  } catch (error) {
    dispatch(errorHandle(error));
    return 0;
  }
};

export const deleteDraftDatasets =
  (orgId, id, datasetIds) => async (dispatch, getStore) => {
    try {
      const { draftDatasets, pathKey } = getStore().aiBot;

      await AiBotService.deleteDataset(orgId, id, datasetIds);

      dispatch(
        setData({
          isDraft: true,
        }),
      );

      dispatch(
        setState({
          draftDatasets: draftDatasets.filter(
            d => !datasetIds.find(datasetId => datasetId === d?.id),
          ),
        }),
      );

      dispatch(
        enqueueSnackbar({
          variant: SnackBarType.SUCCESS,
          message: i18n.t(`${pathKey}:deleteDataset-modal-success`),
        }),
      );
    } catch (error) {
      dispatch(errorHandle(error));
    }
  };

export const getDatasetInitCount = (orgId, id) => async dispatch => {
  try {
    const response = await AiBotService.getDatasetInitCount(orgId, id);

    return response;
  } catch (error) {
    dispatch(errorHandle(error));
    return {};
  }
};

export default aiBotSlice.reducer;
